import { useState } from "react";
import { FiX } from "react-icons/fi";
import { ExerciseInPlan } from "../../../../types";
import { ImageModal } from "../../../conversations/ImageModal";
import { WebViewCompatVideo } from "../../../ui/WebViewCompatVideo";
import { ExerciseSelect } from "./ExerciseSelect";
import utils from "../../../../helpers/utils";
import { getVideoThumbnailUrl } from "../../../../services/videos";

interface ExerciseRowProps {
  exercise: ExerciseInPlan;
  prescIndex: number;
  exerIndex: number;
  professionalId: string;
  onUpdate: (field: keyof ExerciseInPlan, value: string | number) => void;
  onRemove: () => void;
}

const isVideoUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  if (url.startsWith("data:video")) return true;
  return [".mp4", ".webm", ".ogg", ".mov", ".m4v", ".avi", ".mkv", ".m3u8"].some(
    (ext) => url.toLowerCase().includes(ext),
  );
};

function getExerciseImageUrl(
  exercise: ExerciseInPlan,
  width = 240,
  height = 240,
): string | null {
  if (exercise.image && exercise.image.trim() !== "") {
    return utils.getUriImage(exercise.image, "all_view", { width, height });
  }
  if (exercise.image_url && exercise.image_url.trim() !== "") {
    return exercise.image_url;
  }
  return null;
}

function getExerciseVideoSrc(
  exercise: ExerciseInPlan,
  professionalId: string,
): string | null {
  if (!exercise.video || String(exercise.video).trim() === "") {
    return null;
  }
  if (exercise.video.startsWith("data:video")) {
    return exercise.video;
  }
  const profId =
    exercise.professional_id !== undefined &&
    exercise.professional_id !== null
      ? String(exercise.professional_id)
      : professionalId || "all_view";
  return utils.getUriImage(exercise.video, profId);
}

export const ExerciseRow = ({
  exercise,
  prescIndex,
  exerIndex,
  professionalId,
  onUpdate,
  onRemove,
}: ExerciseRowProps) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const isExerciseSelected =
    (exercise.id != null && Number(exercise.id) > 0) ||
    (typeof exercise.name === "string" && exercise.name.trim() !== "");

  const videoSrc = getExerciseVideoSrc(exercise, professionalId);
  const hasVideo = Boolean(videoSrc && isVideoUrl(videoSrc));

  const imageUrl = getExerciseImageUrl(exercise);

  const hasMedia = isExerciseSelected && (hasVideo || Boolean(imageUrl));
  const showVideo = hasVideo && videoSrc;
  const showImage = hasVideo ? false : Boolean(imageUrl);

  return (
    <>
      <div className="relative rounded-xl border border-gray-200 bg-white p-3 shadow-sm transition-all dark:border-gray-700 dark:bg-gray-800 sm:rounded-xl sm:p-4 md:hover:shadow-md">
        {/* Header: Nome do Exercício */}
        <div className="mb-2 sm:mb-3">
          <label
            htmlFor={`exercise-${prescIndex}-${exerIndex}`}
            className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
          >
            Exercício
          </label>
          <ExerciseSelect
            professionalId={professionalId}
            value={exercise.name || ""}
            onChange={(name, id, exerciseData) => {
              onUpdate("name", name);
              onUpdate("id", id);
              if (exerciseData) {
                const imageValue =
                  exerciseData.image_url || exerciseData.image || "";
                if (imageValue) {
                  onUpdate("image", imageValue);
                  onUpdate("image_url", imageValue);
                }
              }
            }}
          />
        </div>

        {/* Grid de Inputs - Mobile: 2 colunas, Tablet+: 4 colunas */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4 lg:gap-4">
          {/* Séries */}
          <div>
            <label
              htmlFor={`sets-${prescIndex}-${exerIndex}`}
              className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Séries
            </label>
            <input
              id={`sets-${prescIndex}-${exerIndex}`}
              type="text"
              inputMode="numeric"
              value={exercise.sets || exercise.series || ""}
              onChange={(e) => onUpdate("sets", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="3"
              className="w-full min-h-[48px] rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-center text-sm font-semibold text-gray-900 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-primary touch-manipulation sm:min-h-0 sm:rounded-lg"
            />
          </div>

          {/* Repetições */}
          <div>
            <label
              htmlFor={`reps-${prescIndex}-${exerIndex}`}
              className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Reps
            </label>
            <input
              id={`reps-${prescIndex}-${exerIndex}`}
              type="text"
              inputMode="numeric"
              value={exercise.reps || exercise.repetitions || ""}
              onChange={(e) => onUpdate("reps", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="12"
              className="w-full min-h-[48px] rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-center text-sm font-semibold text-gray-900 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-primary touch-manipulation sm:min-h-0 sm:rounded-lg"
            />
          </div>

          {/* Carga */}
          <div>
            <label
              htmlFor={`load-${prescIndex}-${exerIndex}`}
              className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Carga
            </label>
            <input
              id={`load-${prescIndex}-${exerIndex}`}
              type="text"
              value={exercise.load || ""}
              onChange={(e) => onUpdate("load", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="20kg"
              className="w-full min-h-[48px] rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-center text-sm font-semibold text-gray-900 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-primary touch-manipulation sm:min-h-0 sm:rounded-lg"
            />
          </div>

          {/* Intervalo */}
          <div>
            <label
              htmlFor={`interval-${prescIndex}-${exerIndex}`}
              className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300"
            >
              Intervalo
            </label>
            <input
              id={`interval-${prescIndex}-${exerIndex}`}
              type="text"
              inputMode="numeric"
              value={exercise.interval || ""}
              onChange={(e) => onUpdate("interval", e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder="60s"
              className="w-full min-h-[48px] rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-center text-sm font-semibold text-gray-900 outline-none transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-primary touch-manipulation sm:min-h-0 sm:rounded-lg"
            />
          </div>
        </div>

        {/* Mídia (vídeo primeiro, senão imagem) - só exibe se exercício selecionado e houver vídeo ou imagem */}
        {hasMedia && (
          <div className="mt-2 sm:mt-3 md:mt-4">
            {showVideo && videoSrc && (
              <div className="relative h-32 w-full overflow-hidden rounded-xl border border-gray-300 bg-gray-900 sm:rounded-lg md:h-40">
                <WebViewCompatVideo
                  src={videoSrc}
                  className="h-full w-full object-contain"
                  muted
                  loop
                  autoPlay
                  poster={
                    imageUrl ||
                    getVideoThumbnailUrl(videoSrc) ||
                    undefined
                  }
                  controls
                />
              </div>
            )}
            {showImage && imageUrl && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageModalOpen(true);
                }}
                className="group relative h-32 w-full overflow-hidden rounded-xl border border-gray-300 bg-gray-50 transition-all active:scale-[0.98] dark:border-gray-600 dark:bg-gray-700 sm:rounded-lg md:h-40 md:hover:border-primary md:hover:shadow-md touch-manipulation"
                aria-label={`Ver imagem de ${exercise.name || "exercício"}`}
              >
                <img
                  src={imageUrl}
                  alt={exercise.name || "Exercício"}
                  className="h-full w-full object-contain transition-transform group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/10 group-hover:opacity-100">
                  <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-900 shadow-lg">
                    Clique para ampliar
                  </span>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Botão Remover */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (
              globalThis.confirm(
                "Tem certeza que deseja remover este exercício?",
              )
            ) {
              onRemove();
            }
          }}
          className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 transition-all active:scale-[0.99] touch-manipulation dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-400 sm:mt-4 sm:min-h-0 sm:rounded-lg md:hover:bg-red-100 md:hover:text-red-700 md:dark:hover:bg-red-900/20"
        >
          <FiX className="h-4 w-4" />
          Remover exercício
        </button>
      </div>

      {/* Modal de Imagem */}
      {imageUrl && (
        <ImageModal
          imageUrl={imageUrl}
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          alt={exercise.name || "Exercício"}
        />
      )}
    </>
  );
};
